import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNotify, usePermissions } from 'react-admin';
import {
  Box, Typography, Card, CardContent, Stack, Chip,
  Table, TableHead, TableRow, TableCell, TableBody,
  TextField, InputAdornment, Collapse, IconButton, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions, Button, CircularProgress,
  Select, MenuItem, FormControl, InputLabel, Switch, FormControlLabel,
} from '@mui/material';
import SearchIcon        from '@mui/icons-material/Search';
import WarningIcon       from '@mui/icons-material/Warning';
import ExpandMoreIcon    from '@mui/icons-material/ExpandMore';
import ExpandLessIcon    from '@mui/icons-material/ExpandLess';
import InventoryIcon     from '@mui/icons-material/Inventory2';
import FactCheckIcon     from '@mui/icons-material/FactCheck';
import CloseIcon         from '@mui/icons-material/Close';
import authFetch from '../utils/authFetch';
import { NAVY, GOLD, tbl, fmtQty, sourceRowLabel, btn, inputSx, typo, clickableA11y } from '../styles/ds';

const LOW = 3;
// Per-variant threshold when set (min_stock), otherwise the default
const isLowStock = v => {
  const qty = Number(v.quantity ?? 0);
  const min = v.min_stock === null || v.min_stock === undefined ? LOW : Number(v.min_stock);
  return qty <= min;
};

// ── Stock count dialog (admin) ────────────────────────────────────────────────
const StockCountDialog = ({ variant, onClose, onSaved }) => {
  const notify = useNotify();
  const [perSupplier, setPerSupplier] = useState(false);
  const [counted,     setCounted]     = useState('');
  const [notes,       setNotes]       = useState('');
  const [saving,      setSaving]      = useState(false);
  const [supRows,     setSupRows]     = useState(null);  // supplier breakdown rows
  const [rowCounts,   setRowCounts]   = useState({});    // { idx: string }

  // Fetch supplier breakdown when switching to per-supplier mode
  useEffect(() => {
    if (!perSupplier || !variant || supRows !== null) return;
    authFetch(`/variants/${variant.id}/supplier-inventory`)
      .then(r => r.json())
      .then(data => {
        const rows = data.rows || [];
        setSupRows(rows);
        const init = {};
        rows.forEach((r, i) => { init[i] = String(Math.round(Number(r.available_qty))); });
        setRowCounts(init);
      })
      .catch(() => setSupRows([]));
  }, [perSupplier, variant, supRows]);

  if (!variant) return null;

  const totalCounted = supRows
    ? supRows.reduce((s, _, i) => s + (Number(rowCounts[i]) || 0), 0)
    : 0;

  const save = async () => {
    setSaving(true);
    try {
      if (!perSupplier) {
        // ── Global mode ───────────────────────────────────────────────────
        if (counted === '' || Number(counted) < 0) {
          notify('נא להזין כמות שנספרה', { type: 'warning' }); setSaving(false); return;
        }
        const res  = await authFetch('/inventory/adjust', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ variant_id: variant.id, counted_qty: Math.round(Number(counted)), notes: notes || null }),
        });
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(body.error || 'שגיאה בעדכון הספירה');
        notify(body.delta === 0
          ? 'אין פער — המלאי תואם'
          : `המלאי עודכן (פער: ${body.delta > 0 ? '+' : ''}${body.delta})`,
          { type: 'success' });
      } else {
        // ── Per-supplier mode ─────────────────────────────────────────────
        if (!supRows || supRows.length === 0) {
          notify('אין נתוני מקור לעדכון', { type: 'warning' }); setSaving(false); return;
        }
        let totalDelta = 0;
        for (const [i, row] of supRows.entries()) {
          const newQty  = Math.round(Number(rowCounts[i] ?? row.available_qty));
          const curQty  = Math.round(Number(row.available_qty));
          if (newQty === curQty) continue;

          const sourceFilter = row.source_type === 'supplier'
            ? { type: 'supplier', supplier_id: row.supplier_id }
            : { type: row.source_type };

          const res  = await authFetch('/inventory/adjust', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              variant_id: variant.id, counted_qty: newQty,
              notes: notes || null, source_filter: sourceFilter,
            }),
          });
          const body = await res.json().catch(() => ({}));
          if (!res.ok) throw new Error(body.error || 'שגיאה');
          totalDelta += Number(body.delta) || 0;
        }
        if (totalDelta === 0) notify('אין שינוי — המלאי תואם', { type: 'info' });
        else notify(`המלאי עודכן (סה"כ פער: ${totalDelta > 0 ? '+' : ''}${totalDelta})`, { type: 'success' });
      }
      onSaved?.();
      onClose();
    } catch (e) { notify(e.message, { type: 'error' }); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onClose={onClose} dir="rtl" maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 700, color: NAVY }}>
        ספירת מלאי
        <IconButton size="small" onClick={onClose} aria-label="סגור"><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Typography fontSize={14} fontWeight={600} mb={0.5}>{variant.name || `וריאנט #${variant.id}`}</Typography>
        <Typography fontSize={12} color="text.secondary" mb={2}>
          במערכת כרגע: {Math.round(Number(variant.quantity))} {variant.unit && variant.unit !== 'יחידה' ? variant.unit : 'יח\''}
        </Typography>

        <FormControlLabel
          sx={{ mb: 2 }}
          control={
            <Switch
              checked={perSupplier}
              onChange={e => setPerSupplier(e.target.checked)}
              size="small"
            />
          }
          label={<Typography fontSize={13}>ספירה לפי ספק / מקור</Typography>}
        />

        {!perSupplier ? (
          <TextField label="כמות שנספרה בפועל" type="number" size="small" autoFocus fullWidth
            value={counted} onChange={e => setCounted(e.target.value)}
            inputProps={{ min: 0, step: 1 }} sx={inputSx} />
        ) : supRows === null ? (
          <Typography fontSize={12} color="text.secondary" mb={1}>טוען נתוני ספקים...</Typography>
        ) : supRows.length === 0 ? (
          <Typography fontSize={12} color="text.secondary" mb={1}>אין מלאי ממוין לפי מקור</Typography>
        ) : (
          <Box mb={1}>
            <Box className="crm-table-scroll" data-mobile-scroll tabIndex={0} role="region" aria-label="טבלה נגללת"><Table size="small" sx={{ mb: 1, border: '1px solid rgba(0,0,0,0.08)', borderRadius: 1.5, overflow: 'hidden' }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: 'rgba(13,29,62,0.06)' }}>
                  <TableCell sx={{ fontSize: 11, fontWeight: 700, color: NAVY }}>מקור</TableCell>
                  <TableCell align="center" sx={{ fontSize: 11, fontWeight: 700, color: NAVY }}>כרגע</TableCell>
                  <TableCell align="center" sx={{ fontSize: 11, fontWeight: 700, color: NAVY }}>ספרתי</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {supRows.map((row, i) => {
                  const cur = Math.round(Number(row.available_qty));
                  const val = rowCounts[i] ?? String(cur);
                  const changed = Math.round(Number(val)) !== cur;
                  return (
                    <TableRow key={i} sx={{ backgroundColor: changed ? 'rgba(255,243,205,0.5)' : (i % 2 === 0 ? '#fff' : '#faf8f5') }}>
                      <TableCell sx={{ fontSize: 12, fontWeight: 600 }}>{sourceRowLabel(row)}</TableCell>
                      <TableCell align="center" sx={{ fontSize: 12, color: 'text.secondary' }}>{cur}</TableCell>
                      <TableCell align="center">
                        <TextField
                          type="number" size="small"
                          value={val}
                          onChange={e => setRowCounts(c => ({ ...c, [i]: e.target.value }))}
                          inputProps={{ min: 0, step: 1, style: { textAlign: 'center', padding: '4px 8px', width: 60 } }}
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table></Box>
            <Typography fontSize={11} color="text.secondary">
              סה"כ שנספרו: <strong>{totalCounted}</strong>
            </Typography>
          </Box>
        )}

        <TextField label="הערה (סיבת הפער)" size="small" multiline rows={2} fullWidth
          value={notes} onChange={e => setNotes(e.target.value)} sx={{ ...inputSx, mt: 2 }} />
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} sx={{ color: '#666' }}>ביטול</Button>
        <Button variant="contained" onClick={save} disabled={saving}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <FactCheckIcon />}
          sx={btn.primary}>
          עדכן מלאי
        </Button>
      </DialogActions>
    </Dialog>
  );
};
const fmtIls  = v => v != null ? `₪${Number(v).toLocaleString('he-IL', { minimumFractionDigits: 2 })}` : '—';
const fmtDate = iso => iso ? new Date(iso).toLocaleDateString('he-IL') : '—';

const stockChip = (qty, unit) => {
  const isZero = qty === 0;
  const isLow  = qty > 0 && qty <= LOW;
  const label  = isZero ? 'אזל' : fmtQty(qty, unit);
  return (
    <Chip label={label} size="small" sx={{
      fontWeight: 700, fontSize: 11, height: 22,
      backgroundColor: isZero ? '#F8D7DA' : isLow ? '#FFF3CD' : '#D4EDDA',
      color:           isZero ? '#721C24' : isLow ? '#856404' : '#155724',
    }} />
  );
};

// ── Batch detail table (lazy-loaded) ─────────────────────────────────────────
const BatchDetail = ({ variantId, open }) => {
  const [batches, setBatches] = useState(null);

  useEffect(() => {
    if (!open || batches !== null) return;
    authFetch(`/inventory_batches?_start=0&_end=50&filter=${encodeURIComponent(JSON.stringify({ variant_id: variantId }))}`)
      .then(r => r.json())
      .then(d => setBatches(Array.isArray(d) ? d : []))
      .catch(() => setBatches([]));
  }, [open, variantId, batches]);

  if (!open) return null;
  if (batches === null) return <Typography fontSize={12} color="text.secondary" px={2} py={1}>טוען אצוות...</Typography>;
  if (batches.length === 0) return <Typography fontSize={12} color="text.secondary" px={2} py={1}>אין אצוות עלות מוגדרות</Typography>;

  return (
    <Box data-mobile-scroll sx={{ overflowX: 'auto', mx: 2, mb: 1.5, borderRadius: 1.5, border: '1px solid rgba(0,0,0,0.07)' }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ backgroundColor: 'rgba(13,29,62,0.06)' }}>
            <TableCell sx={{ fontSize: 11, fontWeight: 700, color: NAVY }}>סוג אצווה</TableCell>
            <TableCell sx={{ fontSize: 11, fontWeight: 700, color: NAVY }} align="center">מקורי</TableCell>
            <TableCell sx={{ fontSize: 11, fontWeight: 700, color: NAVY }} align="center">נותר</TableCell>
            <TableCell sx={{ fontSize: 11, fontWeight: 700, color: NAVY }} align="right">עלות ₪ ליח'</TableCell>
            <TableCell sx={{ fontSize: 11, fontWeight: 700, color: NAVY }} align="center">תאריך</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {batches.map((b, i) => (
            <TableRow key={b.id} sx={tbl.zebraRow(i)}>
              <TableCell>
                <Chip
                  label={b.batch_type === 'purchase' ? 'רכישה' : 'ייצור'} size="small"
                  sx={{ fontSize: 10, height: 18,
                    backgroundColor: b.batch_type === 'purchase' ? '#CCE5FF' : '#D4EDDA',
                    color: b.batch_type === 'purchase' ? '#004085' : '#155724' }}
                />
              </TableCell>
              <TableCell align="center" sx={{ fontSize: 12 }}>{Number(b.original_qty)}</TableCell>
              <TableCell align="center">
                <Typography fontSize={12} fontWeight={700}
                  color={Number(b.remaining_qty) === 0 ? '#bbb' : '#155724'}>
                  {Number(b.remaining_qty)}
                </Typography>
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, fontSize: 12, color: NAVY }}>{fmtIls(b.unit_cost_ils)}</TableCell>
              <TableCell align="center" sx={{ fontSize: 11, color: 'text.secondary' }}>{fmtDate(b.created_at)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

// ── Variant row (expandable) ──────────────────────────────────────────────────
const VariantRow = ({ variant, isLast, isAdmin, onCount }) => {
  const [open, setOpen] = useState(false);
  const qty      = Number(variant.quantity ?? 0);
  const reserved = Number(variant.reserved ?? 0);
  const isLow    = isLowStock(variant);

  return (
    <>
      <Box className="crm-inventory-heading"
        onClick={() => setOpen(o => !o)}
        {...clickableA11y(() => setOpen(o => !o), { ariaLabel: `${variant.name || `#${variant.id}`} — הצג פרטי אצוות`, asButton: true })}
        aria-expanded={open}
        sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          px: 2, py: 1.2, cursor: 'pointer',
          borderBottom: isLast && !open ? 'none' : '1px solid rgba(0,0,0,0.05)',
          backgroundColor: open ? 'rgba(13,29,62,0.03)' : 'transparent',
          '&:hover': { backgroundColor: 'rgba(13,29,62,0.04)' },
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5} flex={1} minWidth={0}>
          <Typography fontSize={13} fontWeight={600} color={isLow && qty > 0 ? '#856404' : qty === 0 ? '#c62828' : '#1a1a2e'}
            sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {variant.name || `#${variant.id}`}
          </Typography>
          {variant.price_ils > 0 && (
            <Typography fontSize={11} color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
              ₪{Number(variant.price_ils).toLocaleString()}
            </Typography>
          )}
          {variant.min_stock !== null && variant.min_stock !== undefined && (
            <Typography fontSize={10} color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
              (מינ' {fmtQty(variant.min_stock)})
            </Typography>
          )}
        </Stack>
        <Stack direction="row" alignItems="center" spacing={1}>
          {reserved > 0 && (
            <Chip label={`משוריין ${fmtQty(reserved)}`} size="small"
              title="כמות בהזמנות פתוחות שטרם סופקו"
              sx={{ fontSize: 10, height: 20, backgroundColor: '#CCE5FF', color: '#004085', fontWeight: 700 }} />
          )}
          {stockChip(qty, variant.unit)}
          {isAdmin && (
            <IconButton size="small" title="ספירת מלאי" aria-label="ספירת מלאי"
              onClick={e => { e.stopPropagation(); onCount(variant); }}
              sx={{ p: 0.3, color: NAVY, opacity: 0.6 }}>
              <FactCheckIcon sx={{ fontSize: 16 }} />
            </IconButton>
          )}
          <IconButton size="small" tabIndex={-1} aria-hidden="true" sx={{ p: 0.3, color: NAVY, opacity: 0.5 }}>
            {open ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
          </IconButton>
        </Stack>
      </Box>
      <Collapse in={open} timeout={200} unmountOnExit>
        <BatchDetail variantId={variant.id} open={open} />
      </Collapse>
    </>
  );
};

// ── Category card (expandable) ────────────────────────────────────────────────
const CategoryCard = ({ product, variants, defaultOpen, isAdmin, onCount }) => {
  const [open, setOpen] = useState(defaultOpen);
  const totalQty   = variants.reduce((s, v) => s + Number(v.quantity ?? 0), 0);
  const lowCount   = variants.filter(isLowStock).length;
  const hasLow     = lowCount > 0;

  return (
    <Card sx={{
      mb: 2, borderRadius: 2, overflow: 'hidden',
      border: hasLow ? `1.5px solid #f5c6cb` : '1.5px solid rgba(13,29,62,0.08)',
      boxShadow: open ? '0 4px 16px rgba(0,0,0,0.1)' : '0 2px 6px rgba(0,0,0,0.06)',
    }}>
      {/* Category header */}
      <Box className="crm-inventory-heading"
        onClick={() => setOpen(o => !o)}
        {...clickableA11y(() => setOpen(o => !o), { ariaLabel: `${product.name} — הצג וריאנטים`, asButton: true })}
        aria-expanded={open}
        sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          px: 3, py: 2, cursor: 'pointer',
          backgroundColor: open ? NAVY : '#fff',
          transition: 'background-color 0.2s',
          '&:hover': { backgroundColor: open ? '#162d5a' : 'rgba(13,29,62,0.04)' },
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <InventoryIcon sx={{ color: open ? '#fff' : NAVY, fontSize: 20, opacity: 0.8 }} />
          <Box>
            <Typography fontWeight={700} fontSize={15} color={open ? '#fff' : NAVY}>
              {product.name}
            </Typography>
            <Typography fontSize={11} color={open ? 'rgba(255,255,255,0.65)' : 'text.secondary'}>
              {variants.length} וריאנטים · {fmtQty(totalQty, variants[0]?.unit)} בסה"כ
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          {hasLow && (
            <Chip
              icon={<WarningIcon sx={{ fontSize: 13, color: '#856404 !important' }} />}
              label={`${lowCount} מלאי נמוך`} size="small"
              sx={{ backgroundColor: '#FFF3CD', color: '#856404', fontWeight: 700, fontSize: 10 }}
            />
          )}
          <Chip
            label={fmtQty(totalQty, variants[0]?.unit)} size="small"
            sx={{
              fontWeight: 700, fontSize: 12,
              backgroundColor: open ? 'rgba(200,169,110,0.25)' : 'rgba(13,29,62,0.08)',
              color: open ? GOLD : NAVY,
            }}
          />
          <IconButton size="small" tabIndex={-1} aria-hidden="true" sx={{ p: 0.3, color: open ? '#fff' : NAVY, opacity: 0.7 }}>
            {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Stack>
      </Box>

      {/* Variant rows */}
      <Collapse in={open} timeout={250}>
        <Box>
          {variants.length === 0 ? (
            <Typography fontSize={13} color="text.secondary" px={3} py={2}>
              אין וריאנטים בקטגוריה זו עדיין
            </Typography>
          ) : (
            variants.map((v, i) => (
              <VariantRow key={v.id} variant={v} isLast={i === variants.length - 1}
                isAdmin={isAdmin} onCount={onCount} />
            ))
          )}
        </Box>
      </Collapse>
    </Card>
  );
};

// ── Main ──────────────────────────────────────────────────────────────────────
export default function InventoryView() {
  const [variants,  setVariants]  = useState([]);
  const [products,  setProducts]  = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [search,    setSearch]    = useState('');
  const [supplierFilter, setSupplierFilter] = useState('');
  const [loading,   setLoading]   = useState(true);
  const [counting,  setCounting]  = useState(null); // variant being stock-counted
  const { permissions } = usePermissions();
  const isAdmin = permissions === 'admin';

  useEffect(() => {
    authFetch('/suppliers?_start=0&_end=200&_sort=name&_order=ASC')
      .then(r => r.json()).then(d => setSuppliers(Array.isArray(d) ? d : [])).catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const variantsUrl = supplierFilter
      ? `/variants?_start=0&_end=500&_sort=name&_order=ASC&filter=${encodeURIComponent(JSON.stringify({ supplier_id: supplierFilter }))}`
      : '/variants?_start=0&_end=500&_sort=name&_order=ASC';
    Promise.all([
      authFetch(variantsUrl).then(r => r.json()),
      authFetch('/products?_start=0&_end=50&_sort=name&_order=ASC').then(r => r.json()),
    ]).then(([vars, prods]) => {
      setVariants(Array.isArray(vars) ? vars : []);
      setProducts(Array.isArray(prods) ? prods : []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [supplierFilter]);

  useEffect(() => { load(); }, [load]);

  // Group variants by product, filtered by search
  const grouped = useMemo(() => {
    const q = search.trim().toLowerCase();
    return products.map(product => {
      const vars = variants.filter(v =>
        v.product_id === product.id &&
        (!q || (v.name || '').toLowerCase().includes(q))
      );
      return { product, variants: vars };
    // When searching — hide empty categories. Without search — show all.
    }).filter(g => q ? g.variants.length > 0 : true);
  }, [variants, products, search]);

  // Summary stats
  const totalVariants = variants.length;
  const totalStock    = variants.reduce((s, v) => s + Number(v.quantity ?? 0), 0);
  const totalValue    = variants.reduce((s, v) => s + Number(v.stock_value_ils ?? 0), 0);
  const totalReserved = variants.reduce((s, v) => s + Number(v.reserved ?? 0), 0);
  const lowStockItems = variants.filter(isLowStock);

  return (
    <Box className="crm-page" p={3} sx={{ width: '100%', boxSizing: 'border-box' }}>
      <Typography {...typo.pageTitle} mb={3}>מצב מלאי</Typography>

      {/* Low stock alert */}
      {lowStockItems.length > 0 && (
        <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 3, borderRadius: 2 }}>
          <Typography fontWeight={700} mb={0.5}>התראות מלאי נמוך ({lowStockItems.length} פריטים)</Typography>
          <Stack className="crm-wrap" direction="row" flexWrap="wrap" gap={0.8}>
            {lowStockItems.map(v => (
              <Chip key={v.id} size="small"
                label={`${v.name} — ${fmtQty(v.quantity ?? 0, v.unit)}`}
                sx={{
                  fontSize: 11,
                  backgroundColor: Number(v.quantity ?? 0) === 0 ? '#F8D7DA' : '#FFF3CD',
                  color:           Number(v.quantity ?? 0) === 0 ? '#721C24' : '#856404',
                  fontWeight: 600,
                }}
              />
            ))}
          </Stack>
        </Alert>
      )}

      {/* Summary strip */}
      <Stack className="crm-stat-row" direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={3}>
        <Card sx={{ borderRadius: 2, flex: 1 }}>
          <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="caption" color="text.secondary">סה"כ וריאנטים</Typography>
            <Typography fontWeight={800} fontSize={22} color={NAVY}>{totalVariants}</Typography>
          </CardContent>
        </Card>
        <Card sx={{ borderRadius: 2, flex: 1 }}>
          <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="caption" color="text.secondary">סה"כ יחידות במלאי</Typography>
            <Typography fontWeight={800} fontSize={22} color="#2e7d32">{totalStock}</Typography>
          </CardContent>
        </Card>
        <Card sx={{ borderRadius: 2, flex: 1 }}>
          <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="caption" color="text.secondary">שווי מלאי (עלות) ₪</Typography>
            <Typography fontWeight={800} fontSize={22} color={NAVY}>
              ₪{Math.round(totalValue).toLocaleString('he-IL')}
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ borderRadius: 2, flex: 1 }}>
          <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="caption" color="text.secondary">משוריין להזמנות פתוחות</Typography>
            <Typography fontWeight={800} fontSize={22} color="#004085">{fmtQty(totalReserved)}</Typography>
          </CardContent>
        </Card>
        <Card sx={{ borderRadius: 2, flex: 1 }}>
          <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="caption" color="text.secondary">מלאי נמוך / אפס</Typography>
            <Typography fontWeight={800} fontSize={22} color={lowStockItems.length > 0 ? '#c62828' : '#2e7d32'}>
              {lowStockItems.length}
            </Typography>
          </CardContent>
        </Card>
      </Stack>

      {/* Search + supplier filter */}
      <Stack className="crm-wrap crm-filter-row" direction="row" spacing={2} mb={2.5} flexWrap="wrap" useFlexGap>
        <TextField size="small" placeholder="חיפוש וריאנט..." aria-label="חיפוש וריאנט"
          value={search} onChange={e => setSearch(e.target.value)}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 17, color: 'text.disabled' }} /></InputAdornment>,
            sx: { ...inputSx, direction: 'rtl' },
          }}
          sx={{ width: '100%', maxWidth: 360 }}
        />
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>סינון לפי ספק</InputLabel>
          <Select value={supplierFilter} label="סינון לפי ספק" onChange={e => setSupplierFilter(e.target.value)}
            sx={{ borderRadius: 2 }}>
            <MenuItem value="">כל הספקים</MenuItem>
            {suppliers.map(s => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
          </Select>
        </FormControl>
      </Stack>

      {/* Categories */}
      {loading ? (
        <Typography color="text.secondary">טוען...</Typography>
      ) : grouped.length === 0 ? (
        <Typography color="text.secondary">לא נמצאו תוצאות</Typography>
      ) : (
        grouped.map(({ product, variants: vars }, i) => (
          <CategoryCard
            key={product.id}
            product={product}
            variants={vars}
            defaultOpen={i === 0}
            isAdmin={isAdmin}
            onCount={setCounting}
          />
        ))
      )}

      {counting && (
        <StockCountDialog
          variant={counting}
          onClose={() => setCounting(null)}
          onSaved={load}
        />
      )}
    </Box>
  );
}
